import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Eye, UserX } from "lucide-react";
import { toast } from "sonner";

interface Coach {
  id: string;
  user_id: string;
  profiles: {
    full_name: string;
    email: string;
  };
}

export const AdminCoachList = () => {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [coachEmail, setCoachEmail] = useState("");
  const [addingCoach, setAddingCoach] = useState(false);
  const [removingCoachId, setRemovingCoachId] = useState<string | null>(null);
  const [coachToRemove, setCoachToRemove] = useState<Coach | null>(null);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("id, user_id")
        .eq("role", "coach");

      if (roleError) throw roleError;

      if (!roleData || roleData.length === 0) {
        setCoaches([]);
        setLoading(false);
        return;
      }

      const userIds = roleData.map(r => r.user_id);
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds);

      if (profileError) throw profileError;

      const coaches = roleData.map(role => {
        const profile = profileData?.find(p => p.id === role.user_id);
        return {
          id: role.id,
          user_id: role.user_id,
          profiles: {
            full_name: profile?.full_name || "Unknown",
            email: profile?.email || "",
          },
        };
      });

      setCoaches(coaches);
    } catch (error) {
      console.error("Error fetching coaches:", error);
      toast.error("Failed to load coaches");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingCoach(true);

    try {
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .eq("email", coachEmail)
        .maybeSingle();

      if (profileError || !profiles) {
        toast.error("User not found. Please ensure they have signed up first.");
        return;
      }

      const { error: roleError } = await supabase
        .from("user_roles")
        .insert({
          user_id: profiles.id,
          role: "coach",
        });

      if (roleError) {
        if (roleError.code === "23505") {
          toast.error("This user is already a coach.");
        } else {
          throw roleError;
        }
        return;
      }

      toast.success(`Successfully added ${profiles.full_name || coachEmail} as a coach!`);
      setCoachEmail("");
      fetchCoaches();
    } catch (error) {
      console.error("Error adding coach:", error);
      toast.error("Failed to add coach. Please try again.");
    } finally {
      setAddingCoach(false);
    }
  };

  const handleRemoveCoach = async () => {
    if (!coachToRemove) return;
    
    setRemovingCoachId(coachToRemove.id);

    try {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("id", coachToRemove.id);

      if (error) throw error;

      toast.success(`Successfully removed ${coachToRemove.profiles.full_name} as a coach`);
      setCoachToRemove(null);
      fetchCoaches();
    } catch (error) {
      console.error("Error removing coach:", error);
      toast.error("Failed to remove coach. Please try again.");
    } finally {
      setRemovingCoachId(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add New Coach
          </CardTitle>
          <CardDescription>
            Enter the email of a registered user to grant them coach access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAddCoach} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="coach-email">User Email</Label>
              <Input
                id="coach-email"
                type="email"
                placeholder="coach@example.com"
                value={coachEmail}
                onChange={(e) => setCoachEmail(e.target.value)}
                required
              />
              <p className="text-sm text-muted-foreground">
                The user must have an account before you can add them as a coach
              </p>
            </div>
            <Button type="submit" disabled={addingCoach}>
              {addingCoach ? "Adding..." : "Add Coach"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Coaches</CardTitle>
          <CardDescription>
            View and manage all vocal coaches
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Loading coaches...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coaches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      No coaches found
                    </TableCell>
                  </TableRow>
                ) : (
                  coaches.map((coach) => (
                    <TableRow key={coach.id}>
                      <TableCell className="font-medium">
                        {coach.profiles.full_name || "Unknown"}
                      </TableCell>
                      <TableCell>{coach.profiles.email}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/coach/${coach.user_id}`)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Calendar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setCoachToRemove(coach)}
                            disabled={removingCoachId === coach.id}
                          >
                            <UserX className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!coachToRemove} onOpenChange={() => setCoachToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Coach Access</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove coach access for{" "}
              <strong>{coachToRemove?.profiles.full_name}</strong>? This will revoke their
              ability to view and manage bookings, but their account will remain active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveCoach}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Coach
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
